import { useState, useEffect } from "react";
import React from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { DatePicker } from "antd";
import locale from 'antd/lib/date-picker/locale/es_ES';
import { productServices } from "../../api/services/products";
import { useSearchParamsDispatch } from "../../context/SearchParamsContext";
import { useCallback } from "react";
import moment from 'moment';
import 'moment/locale/es'; 

const ComparatorHistorical = ({
    comparatorSlot1,
    comparatorSlot2,
    comparatorSlot3,
    comparatorSlot4
}) => {
    const searchParamsDispatch = useSearchParamsDispatch();
    const  [ historicalInfo, setHistoricalInfo ] = useState([]);
    const { RangePicker } = DatePicker;
    const [dateRange, setDateRange] = useState([]);
    const dateFormat = "DD/MM/YYYY";
    const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

    const [minValue, setMinValue] = useState(Infinity);
    const [maxValue, setMaxValue] = useState(-Infinity);

    const [ historicalValues1, setHistoricalValues1 ] = useState([]);
    const [ historicalValues2, setHistoricalValues2 ] = useState([]);
    const [ historicalValues3, setHistoricalValues3 ] = useState([]);
    const [ historicalValues4, setHistoricalValues4 ] = useState([]);
    const [printMinDate, setPrintMinDate] = useState(new Date()); //Fecha mínima producto a comparar
    const [minDateProduct, setMinDateProduct] = useState(''); //Producto con fecha minima a comparar

    const colors = [
        '#cc0000','#e59d27', '#abc111', '#1bb3bc', '#9e3667', '#fecc09'
    ]

    const formatDate = (dateMs) => {
        const msDate = new Date(dateMs);
        const dateFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
        return new Intl.DateTimeFormat('es-ES', dateFormatOptions).format(msDate);
    }

    const filteredData = (!dateRange || dateRange.length === 0)
    ? historicalInfo
    : historicalInfo?.filter((data) => {
        if (!data || !data.dateString) return false; // Verificar que data y data.dateString estén definidos
        const date = moment(data.dateString, dateFormat);
        return date.isBetween(dateRange[0], dateRange[1], null, '[]');
    }) || []; // Devolver un array vacío si historicalInfo es undefined o null

    const createHistoricalInfo = useCallback(() => {
      const dataMap = new Map(); // Mapa con las fechas
      let createMinValue = 100;
      let createMaxValue = -100;
      let base100LiquidValue = 1;
      let dateStart=new Date(); //Fecha minima
      let dateEnd=new Date(); //Fecha máxima
      let dateMapMin = []; // Iterar Fecha mínima a mostrar (deben coincidir)
      let dateMapMax = []; // Iterar Fecha máxima a mostrar (deben coincidir)

      // Variables para encontrar el rango de fechas comunes
      let minDate = new Date(0); // Fecha más temprana de inicio
      let maxDate = new Date(0); // Fecha más reciente de fin
      let minDateSlot = ''; // Producto con la fecha mínima

      // Obtener las fechas seleccionadas del RangePicker
      let selectedStartDate = formatDateToTimestamp(dateRange?.[0]) || null;
      let selectedEndDate = formatDateToTimestamp(dateRange?.[1]) || null;

      const getDates = (data, arrayIndex, initVal, comparatorSlot) => {
          data.forEach(item => {
            const { date } = item;
            if (!dataMap.has(date)) {
                dataMap.set(date, {});
            }
            if(initVal === true){
              dateStart = date;
              initVal = false;
            } else {
                dateEnd = date;
            }
            // Determinar el rango de fechas comunes
            dateMapMin[arrayIndex] = dateStart;
            dateMapMax[arrayIndex] = dateEnd;

            //Inicializamos con el primer producto
            minDateSlot = comparatorSlot.title?.title || '';

            // Si la fecha es menor que la fecha mínima actual, la actualizamos
            if (date > minDate) {
              minDate = date;
              // Guardar el título del producto que tiene esta fecha mínima
              minDateSlot = comparatorSlot.title?.title || '';
            }
            if (date < maxDate) {
                maxDate = date;
            }
          });
      };

      // Calcular base 100 y actualizar el dataMap con el rango común de fechas
      const updateDataMap = (data, arrayIndex, minDate) => {
          data.forEach(item => {
          const { date, liquidValue } = item;
          if ((date === minDate || date === selectedStartDate)) {
            base100LiquidValue = liquidValue;
          }

          if (!dataMap.has(date)) {
              dataMap.set(date, {});
          }

          // Solo considerar las fechas dentro del rango común y fechas seleccionadas
          if ((date >= minDate && date <= maxDate) || ((!selectedStartDate || date >= selectedStartDate) && (!selectedEndDate || date <= selectedEndDate))) {
            // Calculamos el valor relativo en base 100
            dataMap.get(date)[`dateString`] = formatDate(date);
            dataMap.get(date)[`value${arrayIndex}`] = (liquidValue / base100LiquidValue) * 100;

            if (liquidValue < createMinValue) {
              createMinValue = parseFloat((liquidValue/base100LiquidValue)*100);
            }
            if (liquidValue => createMaxValue) {
              createMaxValue = parseFloat((liquidValue/base100LiquidValue)*100);
            }
          } else {
            // Comprueba si la fecha de la entrada esta dentro de las dos fechas para mostrar
            // Solo los datos en los que coinciden todos los productos se deben de mostrar en el grafico
            dataMap.delete(date);
          }
        });
      };

      // Obtener las fechas mínimas y máximas de cada producto
      if (historicalValues1) getDates(historicalValues1, 1, true, comparatorSlot1);
      if (historicalValues2) getDates(historicalValues2, 2, true, comparatorSlot2);
      if (historicalValues3) getDates(historicalValues3, 3, true, comparatorSlot3);
      if (historicalValues4) getDates(historicalValues4, 4, true, comparatorSlot4);

      minDate=Math.max(...dateMapMin.filter(v=>v!=null));
      maxDate=Math.min(...dateMapMax.filter(v=>v!=null));

      // Aplicar la actualización del mapa para cada producto
      if (historicalValues1) updateDataMap(historicalValues1, 1, minDate);
      if (historicalValues2) updateDataMap(historicalValues2, 2, minDate);
      if (historicalValues3) updateDataMap(historicalValues3, 3, minDate);
      if (historicalValues4) updateDataMap(historicalValues4, 4, minDate);

      // Convertir el dataMap en un array para el gráfico
      const newHistoricalInfo = Array.from(dataMap, ([date, values]) => ({ date, ...values }));

      // Actualizar los estados
      setHistoricalInfo(newHistoricalInfo);
      setMinValue(createMinValue);
      setMaxValue(createMaxValue);
      setPrintMinDate(minDate);  // Fecha mínima
      setMinDateProduct(minDateSlot);  // Guardar el título del producto con la fecha mínima
    }, [historicalValues1, historicalValues2, historicalValues3, historicalValues4, comparatorSlot1, comparatorSlot2, comparatorSlot3, comparatorSlot4, dateRange]);

    const parseHistoricalInfo = (rawData, valueSlot) => {
        return rawData.map((rawDataItem) => {
            return {
                date: Date.parse(rawDataItem.fecha),
                liquidValue: rawDataItem.valorLiquidativo,
            }
        })
    }

    const fetchHistoricalData = async (isin) => {
        try {
            return (await productServices.getHistorical(isin));
        } catch {
            return null;
        }
    };

    useEffect(() => {
        setHistoricalValues1(historicalValues1);
        createHistoricalInfo();
    }, [historicalValues1, createHistoricalInfo])

    useEffect(() => {
        setHistoricalValues2(historicalValues2);
        createHistoricalInfo();
    }, [historicalValues2, createHistoricalInfo])

    useEffect(() => {
        setHistoricalValues3(historicalValues3);
        createHistoricalInfo();
    }, [historicalValues3, createHistoricalInfo])

    useEffect(() => {
        setHistoricalValues4(historicalValues4);
        createHistoricalInfo();
    }, [historicalValues4, createHistoricalInfo])

    useEffect(() => {
      if (comparatorSlot1 !== null) {
        fetchHistoricalData(comparatorSlot1.title?.id).then((response) => {
          if (response && response.response) {
            setHistoricalValues1(
              parseHistoricalInfo(response.response, 1).sort(
                (a, b) => a.date - b.date
              )
            );
          } else {
            setHistoricalValues1([]);
          }
        });
      } else {
        setHistoricalValues1([]);
      }
    }, [comparatorSlot1]);

    useEffect(() => {
      if (comparatorSlot2 !== null) {
        fetchHistoricalData(comparatorSlot2.title?.id).then((response) => {
          if (response && response.response) {
            setHistoricalValues2(
              parseHistoricalInfo(response.response, 2).sort(
                (a, b) => a.date - b.date
              )
            );
          } else {
            setHistoricalValues2([]);
          }
        });
      } else {
        setHistoricalValues2([]);
      }
    }, [comparatorSlot2]);

    useEffect(() => {
      if (comparatorSlot3 !== null) {
        fetchHistoricalData(comparatorSlot3.title?.id).then((response) => {
          if (response && response.response) {
            setHistoricalValues3(
              parseHistoricalInfo(response.response, 3).sort(
                (a, b) => a.date - b.date
              )
            );
          } else {
            setHistoricalValues3([]);
          }
        });
      } else {
        setHistoricalValues3([]);
      }
    }, [comparatorSlot3]);

    useEffect(() => {
      if (comparatorSlot4 !== null) {
        fetchHistoricalData(comparatorSlot4.title?.id).then((response) => {
          if (response && response.response) {
            setHistoricalValues4(
              parseHistoricalInfo(response.response, 4).sort(
                (a, b) => a.date - b.date
              )
            );
          } else {
            setHistoricalValues4([]);
          }
        });
      } else {
        setHistoricalValues4([]);
      }
    }, [comparatorSlot4]);

    //Convertir fecha RangePicker con hora a timestamp
    const formatDateToTimestamp = (date) => {
      // Formatear la fecha a "DD/MM/YYYY"
      const formattedDate = moment(date).format('DD/MM/YYYY');
      // Convertir la fecha formateada a un timestamp
      const timestamp = moment(formattedDate, 'DD/MM/YYYY').startOf('day').valueOf();
      return timestamp;
    };

    return(
        <div style={{width: '100%'}}>
            <div className="d-flex">
                {<div style={{marginTop: 10}}>
                    <div className='search-center-dropdown main-search'
                        style={{maxWidth: 350, marginBottom: 10}}
                        >
                        <RangePicker
                                locale={locale}
                                format={dateFormat}
                                className="date-picker d-flex"
                                onChange={(dates) => {
                                  setDateRange(dates)
                                  searchParamsDispatch({ comparatorHistoricalRange: dates });
                                }}
                            />
                    </div>
                </div>}
                    {
                    dateRange?.length > 0 &&
                    <span style={{marginLeft: 30, top: 10, position: 'relative', marginTop:10}}>{[dateRange[0].date()]+ ' ' + months[dateRange[0].month()]+ ' ' + dateRange[0].year()} - {[dateRange[1].date()]+ ' ' + months[dateRange[1].month()] + ' ' + dateRange[1].year()} </span>
                    }
            </div>

            <div>
            <div>
              {
                dateRange?.length === 0 &&
                <p>Se muestran datos a partir del {moment(printMinDate).format('DD/MM/YYYY')}, que comienza el producto {minDateProduct}</p>
              }
            </div>
            </div>
            <ResponsiveContainer width="100%" height={600}>
                <LineChart
                    data={filteredData}
                    >
                    <CartesianGrid strokeDasharray="3 3" />
                    <Legend />
                    <Tooltip />
                    <XAxis dataKey="dateString" />
                    <YAxis
                        type="number"
                        domain={[minValue , maxValue]}
                        tickFormatter={(tick) => tick.toFixed(2)}
                    />
                    {comparatorSlot1 && <Line key={1} name={comparatorSlot1.title?.title} dataKey="value1" stroke={colors[0]}/>}
                    {comparatorSlot2 && <Line key={2} name={comparatorSlot2.title?.title} dataKey="value2" stroke={colors[1]}/>}
                    {comparatorSlot3 && <Line key={3} name={comparatorSlot3.title?.title} dataKey="value3" stroke={colors[2]}/>}
                    {comparatorSlot4 && <Line key={4} name={comparatorSlot4.title?.title} dataKey="value4" stroke={colors[3]}/>}
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}

export default ComparatorHistorical;